import { useCallback, useEffect, useRef, useState } from 'react'
import type { CarouselBlock } from '@publiqa/shared'
import { SmartLink } from '../context'
import { Picture } from '../ui'
import { SectionHead } from './Blocks'

/**
 * Carrousel sans dépendance : défilement automatique suspendu au survol, au
 * focus clavier et quand l'utilisateur demande moins d'animations.
 */
export function CarouselView({ b }: { b: CarouselBlock }) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const count = b.slides.length
  const touchX = useRef<number | null>(null)

  const go = useCallback((i: number) => setIndex(((i % count) + count) % count), [count])

  useEffect(() => {
    if (!b.autoplay || paused || count < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = window.setInterval(() => setIndex((i) => (i + 1) % count), b.interval * 1000)
    return () => window.clearInterval(t)
  }, [b.autoplay, b.interval, paused, count])

  if (!count) return null
  const current = Math.min(index, count - 1)

  return (
    <section className="b-section b-carousel">
      <div className="container">
        <SectionHead title={b.title} />
        <div
          className="carousel"
          role="region"
          aria-roledescription="carrousel"
          aria-label={b.title || 'Carrousel'}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') go(current - 1)
            if (e.key === 'ArrowRight') go(current + 1)
          }}
          onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
          onTouchEnd={(e) => {
            const start = touchX.current
            const end = e.changedTouches[0]?.clientX
            if (start !== null && end !== undefined && Math.abs(end - start) > 40)
              go(current + (end < start ? 1 : -1))
            touchX.current = null
          }}
        >
          <div className="carousel-track" style={{ transform: `translateX(-${current * 100}%)` }}>
            {b.slides.map((s, i) => (
              <figure
                className="slide"
                key={i}
                aria-hidden={i !== current}
                aria-roledescription="diapositive"
                aria-label={`${i + 1} sur ${count}`}
              >
                <Picture image={s.image} label={s.image ? undefined : s.title} />
                {(s.title || s.caption) && (
                  <figcaption>
                    {s.title && (
                      <strong>
                        {s.href ? (
                          <SmartLink href={s.href} tabIndex={i === current ? 0 : -1}>
                            {s.title}
                          </SmartLink>
                        ) : (
                          s.title
                        )}
                      </strong>
                    )}
                    {s.caption && <span>{s.caption}</span>}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
          {count > 1 && (
            <>
              <button
                type="button"
                className="carousel-nav prev"
                onClick={() => go(current - 1)}
                aria-label="Précédent"
              >
                ‹
              </button>
              <button
                type="button"
                className="carousel-nav next"
                onClick={() => go(current + 1)}
                aria-label="Suivant"
              >
                ›
              </button>
              <div className="carousel-dots">
                {b.slides.map((_, i) => (
                  <button
                    type="button"
                    key={i}
                    className={i === current ? 'on' : ''}
                    onClick={() => go(i)}
                    aria-label={`Aller à la diapositive ${i + 1}`}
                    aria-current={i === current}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
